// components/AddPatientFilters.jsx
import React, { useState, useEffect, useRef } from "react";
import { FaPlusCircle } from "react-icons/fa";
import { RxCrossCircled } from "react-icons/rx";
import { DatePicker } from "rsuite";

const Button = ({ children, className = "", ...props }) => {
  return (
    <button {...props} className={` ${className}`}>
      {children}
    </button>
  );
};  

const AddPatientFilters = ({
  filters,
  setSelectedFilters,
  selectedFilters,
}) => {
  const [selectedFilterKeys, setSelectedFilterKeys] = useState(new Set());
  const [selectedFilterOptions, setSelectedFilterOptions] = useState(new Set());
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const toggleFilterKey = (key) => {
    const updated = new Set(selectedFilterKeys);
    updated.has(key) ? updated.delete(key) : updated.add(key);
    setSelectedFilterKeys(updated);
  };

  const handleApply = () => {
    setActiveDropdown(null);
  };

  const handleReset = () => {
    // clear local selections
    setSelectedFilterKeys(new Set());
    setSelectedFilterOptions({}); // << clear all child options too

    // clear global selections in parent
    setSelectedFilters({}); // wipes everything from parent's filter data

    // close dropdown
    setActiveDropdown(null);
  };

  const removeTag = (key) => {
    setSelectedFilters((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key))
    );
    const updated = new Set(selectedFilterKeys);
    updated.delete(key);
    setSelectedFilterKeys(updated);
    if (activeDropdown === key) setActiveDropdown(null);
  };

  useEffect(() => {
    const handler = (e) => {
      const isInsideDropdown =
        dropdownRef.current && dropdownRef.current.contains(e.target);
      const isInsideDatePicker = e.target.closest(".rs-picker-popup"); // <-- allow DatePicker popup clicks

      if (!isInsideDropdown && !isInsideDatePicker) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleFilterOption = (key, value, inputType, operator, datePart) => {
    if (!inputType) {
      // Multi-select mode
      setSelectedFilterOptions((prev) => {
        const currentSet = new Set(prev[key]?.values || []);

        currentSet.has(value)
          ? currentSet.delete(value)
          : currentSet.add(value);

        if (currentSet.size === 0) {
          const { [key]: _, ...rest } = prev;
          return rest;
        }

        return {
          ...prev,
          [key]: { values: currentSet, operator: operator ?? "Equals" },
        };
      });
    }

    if (inputType === "single-select") {
      setSelectedFilterOptions((prev) => {
        const currentSet = new Set(prev[key]?.values || []);

        if (currentSet.has(value)) {
          // Toggle off
          const { [key]: _, ...rest } = prev;
          return rest;
        }

        // Replace with only this value
        const newSet = new Set([value]);
        return {
          ...prev,
          [key]: { values: newSet, operator: operator ?? "Equals" },
        };
      });
    }

    if (inputType === "date-range") {
      // datePart is either "start" or "end"
      setSelectedFilterOptions((prev) => {
        const existingRange = Array.isArray(prev[key]?.values)
          ? prev[key].values
          : [null, null];

        let [start, end] = existingRange;

        if (datePart === "start") start = value || null;
        if (datePart === "end") end = value || null;

        // If both cleared → remove key entirely
        if (!start && !end) {
          const { [key]: _, ...rest } = prev;
          return rest;
        }

        return {
          ...prev,
          [key]: { values: [start, end], operator: operator ?? "Between" },
        };
      });
    }
  };

  const handleApplyFilters = () => {
    setSelectedFilters((prev) => {
      const updated = { ...prev, ...selectedFilterOptions };
      return updated;
    });
    setActiveDropdown(null);
  };

  const handleResetfilter = (key) => {
    setSelectedFilterOptions((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
    setSelectedFilters((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const filterStyle = `
    /* Filter Tag - Clean Design */
    .filter-tag {
      background: #f8f9fa;
      color: #495057;
      font-size: 13px;
      font-weight: 500;
      padding: 8px 12px;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
      position: relative;
      overflow: hidden;
    }

    .filter-tag::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(108, 117, 125, 0.1), transparent);
      transition: left 0.5s ease;
    }

    .filter-tag:hover::before {
      left: 100%;
    }

    .filter-tag:hover {
      background: #e9ecef;
      border-color: #ced4da;
      color: #343a40;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .filter-tag.active {
      background: #6c757d;
      color: white;
      border-color: #6c757d;
      box-shadow: 0 2px 8px rgba(108, 117, 125, 0.25);
    }

    /* Add Filter Button - Green Theme */
    .add-filter-btn {
      background: #f8f9fa;
      color: #28a745;
      font-size: 13px;
      font-weight: 600;
      padding: 8px 16px;
      border: 2px solid #28a745;
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
      position: relative;
      overflow: hidden;
      min-width: 120px;
      justify-content: center;
    }

    .add-filter-btn::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(40, 167, 69, 0.1), transparent);
      transition: left 0.5s ease;
    }

    .add-filter-btn:hover::before {
      left: 100%;
    }

    .add-filter-btn:hover {
      background: #e8f5e8;
      border-color: #20c997;
      color: #20c997;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.15);
    }

    .add-filter-btn.active {
      background: #f8f9fa;
      color: #28a745;
      border-color: #28a745;
      box-shadow: 0 2px 8px rgba(40, 167, 69, 0.25);
    }

    .add-filter-btn.active::before {
      display: none;
    }

    .add-filter-btn.has-selection {
      background: #f1f3f4;
      border-color: #28a745;
      color: #28a745;
    }

    .add-filter-btn.has-selection:hover {
      background: #e8f5e8;
      border-color: #20c997;
      color: #20c997;
    }

    .add-filter-btn.has-selection.active {
      background: #f8f9fa;
      color: #28a745;
      border-color: #28a745;
    }

    .add-filter-btn .selection-count {
      background: #dc3545;
      color: white;
      font-size: 11px;
      font-weight: 600;
      min-width: 18px;
      height: 18px;
      border-radius: 9px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 4px;
      animation: countBounce 0.3s ease-out;
    }

    /* Selection count badge */
    .selection-count {
      background: #dc3545;
      color: white;
      font-size: 11px;
      font-weight: 600;
      min-width: 18px;
      height: 18px;
      border-radius: 9px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 4px;
      animation: countBounce 0.3s ease-out;
    }

    @keyframes countBounce {
      0% { transform: scale(0.3); opacity: 0; }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); opacity: 1; }
    }

    .filter-tag.has-selection {
      background: #f1f3f4;
      border-color: #adb5bd;
      color: #495057;
    }

    .filter-tag.has-selection:hover {
      background: #e9ecef;
      border-color: #6c757d;
    }

    .filter-tag.has-selection.active {
      background: #6c757d;
      color: white;
      border-color: #6c757d;
    }

    .filter-tag.has-selection.active .selection-count {
      background: rgba(255, 255, 255, 0.9);
      color: #6c757d;
    }

    /* Remove button styling */
    .remove-btn {
      color: #dc3545;
      transition: all 0.2s ease;
      font-size: 14px;
    }

    .remove-btn:hover {
      color: #c82333;
      transform: scale(1.1);
    }

    /* Fix cross icon visibility on active/gray background */
    .filter-tag.active .remove-btn {
      color: rgba(255, 255, 255, 0.9);
    }

    .filter-tag.active .remove-btn:hover {
      color: white;
      transform: scale(1.1);
    }

    .filter-tag.has-selection .remove-btn {
      color: #dc3545;
    }

    .filter-tag.has-selection.active .remove-btn {
      color: rgba(255, 255, 255, 0.9);
    }

    .filter-tag.has-selection.active .remove-btn:hover {
      color: white;
    }

    /* Dropdown Animation */
    .dropdown-container {
      position: absolute;
      z-index: 30;
      margin-top: 8px;
      width: 280px;
      background: white;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
      animation: dropdownSlide 0.2s ease-out forwards;
      transform-origin: top;
    }

    @keyframes dropdownSlide {
      0% { 
        opacity: 0; 
        transform: translateY(-10px) scale(0.95); 
      }
      100% { 
        opacity: 1; 
        transform: translateY(0) scale(1); 
      }
    }

    /* Dropdown Header */
    .dropdown-header {
      padding: 16px;
      border-bottom: 1px solid #f1f3f4;
      background: #fafbfc;
      border-radius: 12px 12px 0 0;
    }

    .dropdown-title {
      font-size: 14px;
      font-weight: 600;
      color: #495057;
      margin: 0;
    }

    .dropdown-title.green {
      color: #28a745;
    }

    /* Options Container */
    .options-container {
      padding: 12px 0;
      max-height: 240px;
      overflow-y: auto;
    }

    /* Date Range Container */
    .date-range-container {
      padding: 16px;
    }

    .date-input-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-bottom: 12px;
    }

    .date-label {
      font-size: 13px;
      font-weight: 600;
      color: #495057;
      margin-bottom: 4px;
    }

    .date-input {
      padding: 8px 12px;
      border: 1px solid #dee2e6;
      border-radius: 6px;
      font-size: 13px;
      transition: all 0.2s ease;
    }

    .date-input:focus {
      outline: none;
      border-color: #28a745;
      box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.1);
    }

    /* Custom Checkbox Design */
    .checkbox-item {
      display: flex;
      align-items: center;
      padding: 8px 16px;
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
      overflow: hidden;
    }

    .checkbox-item::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(108, 117, 125, 0.05), transparent);
      transition: left 0.4s ease;
    }

    .checkbox-item:hover::before {
      left: 100%;
    }

    .checkbox-item:hover {
      background-color: #f8f9fa;
    }

    .checkbox-item.selected {
      background-color: #f1f3f4;
      border-left: 3px solid #6c757d;
    }

    .checkbox-item.selected .checkbox-label {
      color: #495057;
      font-weight: 500;
    }

    .custom-checkbox {
      position: relative;
      width: 18px;
      height: 18px;
      margin-right: 12px;
      flex-shrink: 0;
    }

    .custom-checkbox input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      width: 100%;
      height: 100%;
    }

    .checkmark {
      position: absolute;
      top: 0;
      left: 0;
      height: 18px;
      width: 18px;
      background-color: #fff;
      border: 2px solid #dee2e6;
      border-radius: 4px;
      transition: all 0.2s ease;
    }

    .custom-checkbox:hover .checkmark {
      border-color: #6c757d;
      background-color: #f8f9fa;
    }

    .custom-checkbox input:checked ~ .checkmark {
      background-color: #6c757d;
      border-color: #6c757d;
    }

    .checkmark:after {
      content: "";
      position: absolute;
      display: none;
      left: 5px;
      top: 2px;
      width: 4px;
      height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }

    .custom-checkbox input:checked ~ .checkmark:after {
      display: block;
    }

    .checkbox-label {
      font-size: 14px;
      color: #495057;
      line-height: 1.4;
      flex: 1;
    }

    /* Action Buttons */
    .actions-container {
      padding: 12px 16px;
      border-top: 1px solid #f1f3f4;
      background: #fafbfc;
      border-radius: 0 0 12px 12px;
      display: flex;
      gap: 8px;
      justify-content: flex-end;
    }

    .apply-btn {
      background: #6c757d;
      color: white;
      border: none;
      font-size: 13px;
      font-weight: 500;
      border-radius: 6px;
      padding: 8px 16px;
      transition: all 0.2s ease;
      cursor: pointer;
    }

    .apply-btn:hover {
      background: #5a6268;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(108, 117, 125, 0.25);
    }

    .reset-btn {
      background: transparent;
      color: #6c757d;
      border: 1px solid #dee2e6;
      font-size: 13px;
      font-weight: 500;
      border-radius: 6px;
      padding: 8px 16px;
      transition: all 0.2s ease;
      cursor: pointer;
    }

    .reset-btn:hover {
      background: #f8f9fa;
      border-color: #6c757d;
    }

    /* Scrollbar Styling */
    .options-container::-webkit-scrollbar {
      width: 4px;
    }

    .options-container::-webkit-scrollbar-track {
      background: #f1f3f4;
      border-radius: 2px;
    }

    .options-container::-webkit-scrollbar-thumb {
      background: #dee2e6;
      border-radius: 2px;
    }

    .options-container::-webkit-scrollbar-thumb:hover {
      background: #ced4da;
    }
       .rs-picker-popup {
    z-index: 9999 !important;
  }
  .rs-input-group {
    border-radius: 8px !important;
    border: #e5e5ea solid 1px !important;
    height: 39px;
    overflow: hidden;
    background: white;
  }
  .rs-input-group .rs-input {
    border-radius: 8px !important;
    border: none !important;
    background: none !important;
    box-shadow: none !important;
  }
  .rs-input-group .rs-input:focus {
    outline: none;
    box-shadow: none;
  }
  .rs-picker-toggle {
    border-radius: 8px !important;
  }
  .rs-input-group-inside .rs-input {
    border: none !important;
    border-radius: 8px !important;
    box-shadow: none !important;
    background: none !important;
  }
  `;

  return (
    <>
      <style>{filterStyle}</style>
      <div
        className="flex items-center flex-wrap gap-2 relative"
        ref={dropdownRef}
      >
        {/* Selected Filters as Tags */}
        {[...selectedFilterKeys].map((key) => {
          const filter = filters.find((f) => f.key === key);
          const selectionCount = selectedFilterOptions[key]?.values?.size || 0;
          const hasSelection = selectionCount > 0;

          return (
            <div className="relative" key={key}>
              <button
                className={`filter-tag ${
                  activeDropdown === key ? "active" : ""
                } ${hasSelection ? "has-selection" : ""}`}
                onClick={() =>
                  setActiveDropdown((prev) => (prev === key ? null : key))
                }
              >
                <span className="text-[12px] font-semibold">
                  {filter.label}
                </span>
                {hasSelection && (
                  <span className="selection-count">{selectionCount}</span>
                )}

                <RxCrossCircled
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetfilter(key);
                    removeTag(key);
                  }}
                  className="remove-btn cursor-pointer"
                />
              </button>

              {/* Dynamic Dropdown */}
              {activeDropdown === key && (
                <div className="dropdown-container">
                  <div className="dropdown-header">
                    <h3 className="dropdown-title">Filter by {filter.label}</h3>
                  </div>

                  {filter.inputType === "date-range" ? (
                    <div className="date-range-container">
                      <div className="date-input-group">
                        <label className="date-label">From</label>
                        <DatePicker
                          format="dd-MMM-yyyy"
                          oneTap
                          placeholder="dd-mmm-yyyy"
                          style={{ width: "100%", borderRadius: "none" }}
                          menuClassName="z-[9999]"
                          className="mb-0 mt-0 border-[0px]"
                          onChange={(date) => {
                            if (date) {
                              const formattedDate = date
                                .toLocaleDateString("en-CA")
                                .split("T")[0]; // yyyy-mm-dd format
                              toggleFilterOption(
                                key,
                                formattedDate,
                                filter.inputType,
                                filter.operator,
                                "start"
                              );
                              const currentRange = selectedFilterOptions[key]
                                ?.values || [null, null];
                              if (
                                currentRange[1] &&
                                new Date(formattedDate) >=
                                  new Date(currentRange[1])
                              ) {
                                toggleFilterOption(
                                  key,
                                  "",
                                  filter.inputType,
                                  filter.operator,
                                  "end"
                                );
                              }
                            } else {
                              toggleFilterOption(
                                key,
                                "",
                                filter.inputType,
                                filter.operator,
                                "start"
                              );
                            }
                          }}
                        />
                      </div>
                      <div className="date-input-group">
                        <label className="date-label">To</label>
                        <DatePicker
                          format="dd-MMM-yyyy"
                          oneTap
                          placeholder="dd-mmm-yyyy"
                          style={{ width: "100%", borderRadius: "none" }}
                          menuClassName="z-[9999]"
                          className="mb-0 mt-0 border-[0px]"
                          disabledDate={(date) => {
                            const currentRange = selectedFilterOptions[key]
                              ?.values || [null, null];
                            if (!currentRange) return false;
                            const startDate = new Date(currentRange);
                            return date <= startDate;
                          }}
                          onChange={(date) => {
                            if (date) {
                              const formattedDate = date
                                .toLocaleDateString("en-CA")
                                .split("T")[0];
                              toggleFilterOption(
                                key,
                                formattedDate,
                                filter.inputType,
                                filter.operator,
                                "end"
                              );
                            } else {
                              toggleFilterOption(
                                key,
                                "",
                                filter.inputType,
                                filter.operator,
                                "end"
                              );
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="options-container">
                      {filter.options?.map((opt) => (
                        <div
                          key={opt}
                          className={`checkbox-item ${
                            selectedFilterOptions[key]?.values?.has(opt)
                              ? "selected"
                              : ""
                          }`}
                          onClick={() =>
                            toggleFilterOption(
                              key,
                              opt,
                              filter.inputType,
                              filter.operator
                            )
                          }
                        >
                          <label className="custom-checkbox">
                            <input
                              type="checkbox"
                              checked={
                                selectedFilterOptions[key]?.values
                                  ? selectedFilterOptions[key]?.values?.has(opt)
                                  : false
                              }
                              onChange={() => toggleFilterOption(key, opt, filter.inputType, filter.operator)}
                            />
                            <span className="checkmark"></span>
                          </label>
                          <span className="checkbox-label">{opt}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="actions-container">
                    <button
                      className="reset-btn"
                      onClick={() => handleResetfilter(key)}
                    >
                      Reset
                    </button>
                    <button className="apply-btn" onClick={handleApplyFilters}>
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Add Filter Button */}
        <div className="relative inline-block text-left">
          <button
            onClick={() => setActiveDropdown("add")}
            className={`add-filter-btn ${
              activeDropdown === "add" ? "active" : ""
            } ${selectedFilterKeys.size > 0 ? "has-selection" : ""}`}
          >
            <span>Add Filters</span>
            <FaPlusCircle />
            {selectedFilterKeys.size > 0 && (
              <span className="selection-count">{selectedFilterKeys.size}</span>
            )}
          </button>

         {activeDropdown === "add" && (
  <div className="dropdown-container">
    <div className="dropdown-header">
      <h3 className="dropdown-title green">Select Filters</h3>
    </div>

    <div className="options-container">
      {filters.map((f) => (
  <div
    key={f.key}
    className={`checkbox-item ${selectedFilterKeys.has(f.key) ? "selected" : ""}`}
    onClick={() => toggleFilterKey(f.key)}
  >
    <label className="custom-checkbox" onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        checked={selectedFilterKeys.has(f.key)}
        onChange={() => toggleFilterKey(f.key)} 
      />
      <span className="checkmark"></span>
    </label>
    <span className="checkbox-label">{f.label}</span>
  </div>
))}
    </div>

    <div className="actions-container">
      <button className="reset-btn" onClick={handleReset}>
        Reset
      </button>
      <button className="apply-btn" onClick={handleApply}>
        Apply
      </button>
    </div>
  </div>
)}

        </div>
      </div>
    </>
  );
};

export default AddPatientFilters;
